import { FilterListRounded } from "@mui/icons-material";
import { IconButton, Select, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { TSetUrlParamsArgs, useXoraiaValue } from "../../../../hooks/useXoraia";
import { generateBorder } from "../../../../lib/generateBorder";
import { generateRadius } from "../../../../lib/generateRadius";
import { generateSide } from "../../../../lib/generateSide";
import { IArticleData } from "../../../../utils/interfaces/common/XArticle.interfaces";
import { IXCategoryFilterProps } from "../../../../utils/interfaces/containers/XCategoryFilter.interfaces";

const GridSorter: React.FC<
  IXCategoryFilterProps & {
    onChange: (args: TSetUrlParamsArgs) => Promise<void>;
    urlParams: TSetUrlParamsArgs;
    articles?: IArticleData[];
    articlesCount: number;
    pageCount: number;
    setOpenFilter: (open: boolean) => void;
  }
> = ({
  gridSorterBgColor,
  gridSorterFontSize = "16px",
  gridSorterRadius,
  gridSorterPadding = {
    Top: "15px",
    Bottom: "15px",
    Left: "20px",
    Right: "20px",
  },
  gridSorterMargin,
  gridSorterColor = "#000000",
  gridSorterSelectBgColor = "#FFFFFF",
  gridSorterSelectColor = "#000000",
  gridSorterSelectBorder = { width: "1px", color: "#EEEEEE" },
  gridSorterSelectRadius,
  articlesCount,
  pageLimit = 8,
  urlParams,
  pageCount,
  onChange,
  setOpenFilter,
}) => {
  const breakpoint = useBreakpoint();
  const { env } = useXoraiaValue();
  const currentPage = urlParams.p ?? 1;
  const fromCount = (currentPage - 1) * pageLimit;
  let toCount = currentPage * pageLimit;

  if (pageCount === currentPage) {
    toCount = toCount - (articlesCount % pageLimit);
  }

  const isSmall = ["mobile", "tablet"].includes(breakpoint);

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      id="FilterGridHeader"
      sx={{
        bgcolor: gridSorterBgColor,
        fontSize: gridSorterFontSize,
        borderRadius: generateRadius(gridSorterRadius),
        padding: generateSide("padding", gridSorterPadding),
        margin: generateSide("margin", gridSorterMargin),
        color: gridSorterColor,

        ...(isSmall ? { px: 0 } : {}),

        "& .MuiNativeSelect-select": {
          bgcolor: gridSorterSelectBgColor,
          color: gridSorterSelectColor,
          border: generateBorder(gridSorterSelectBorder),
          borderRadius: generateRadius(gridSorterSelectRadius),
          py: 0.5,
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          lineHeight: "1em",

          "& fieldset": {
            border: "none",
          },
        },
      }}
    >
      {isSmall && (
        <IconButton
          size="small"
          onClick={env === "dev" ? undefined : () => setOpenFilter(true)}
          color="inherit"
        >
          <FilterListRounded color="inherit" />
        </IconButton>
      )}
      {isSmall && breakpoint === "mobile" && (
        <Typography flex={1} variant="caption">
          {fromCount}-{toCount}
        </Typography>
      )}
      {(!isSmall || breakpoint === "tablet") && (
        <Typography flex={1}>
          Affichage de {fromCount} à {toCount} sur {articlesCount} résultats.
        </Typography>
      )}
      <Stack spacing={1} direction="row" alignItems="center">
        {!isSmall && <Typography fontSize="inherit">Trier par</Typography>}
        <Select
          native
          size="small"
          value={urlParams.s ?? "createdAt"}
          onChange={(e) => {
            onChange({ s: e.target.value as any });
          }}
        >
          <option value="createdAt">Trier par Date</option>
          <option value="price">Trier par Prix</option>
          <option value="name">Trier par Nom</option>
          <option value="rating">Trier par Notes</option>
        </Select>
      </Stack>
      <Stack spacing={1} direction="row" alignItems="center">
        {!isSmall && <Typography fontSize="inherit">Sens</Typography>}
        <Select
          native
          size="small"
          value={urlParams.o ?? "asc"}
          onChange={(e) => {
            onChange({ o: e.target.value as any });
          }}
        >
          <option value="asc">Croissant</option>
          <option value="desc">Décroissant</option>
        </Select>
      </Stack>
    </Stack>
  );
};

export default GridSorter;
